import { useEffect, useRef, useState } from 'react';
import { AppState, Vibration, Platform } from 'react-native';
import { createAudioPlayer, setAudioModeAsync, AudioPlayer } from 'expo-audio';
import { useAuth } from '../context/AuthContext';
import { useBlockedUsers } from '../context/BlockedUsersContext';
import { loadMutedSounds, isSoundMuted } from '../services/chat';
import { chatPresenceHub, MessageNotifyEvent } from '../services/chat/presenceHub';
import { parseMasterProfileIdFromChannel } from '../utils/streamChannelId';
import { getActiveSessionExit } from '../live-session/sessionExitRegistry';
import { BrandToast } from './BrandToast';

const SOUND = require('../../assets/sounds/notification.wav');

/**
 * Foreground new-message notifier. On native, plays a short chime + vibration
 * when a new incoming message arrives (in-app notification, à la Telegram) and
 * renders nothing. On web (no chime), surfaces a soft `BrandToast` for new
 * community-discussion messages instead. Background notifications are delivered
 * by system push, not here.
 *
 * CORE-621 (toward CORE-377) — driven by Agape.ChatApi's global `MessageNotify`
 * (a per-user SignalR fan-out via {@link chatPresenceHub}), replacing the
 * GetStream websocket `message.new` / `notification.message_new` this used to
 * listen on. That was the last surface still reading the Stream client, so with
 * this repoint `StreamChatContext` can be deleted (CORE-618). The server already
 * excludes the caller as sender and only reaches connected clients; here we
 * still honour own-message / blocked / per-chat «sound off» filters.
 */
export function MessageNotifier() {
  const { isAuthenticated, user } = useAuth();
  const { isBlocked } = useBlockedUsers();
  const playerRef = useRef<AudioPlayer | null>(null);
  // CORE-160 (approach A) — web has no chime; surface a soft in-app toast for
  // new community-discussion messages instead, driven by the same notify stream.
  const [communityToast, setCommunityToast] = useState<string | null>(null);
  // Bare app-user guid; senderId from the server is the same bare form. The
  // strip is defensive against any `user_`-prefixed id leaking through.
  const myId = (user?.id || '').replace(/^user_/, '');

  // Native-only: the in-app chime + vibration are a mobile feature. Warm up the
  // audio player (web stays a pure no-op — the Platform guard is inside).
  useEffect(() => {
    if (Platform.OS === 'web') return;
    loadMutedSounds();
    const player = createAudioPlayer(SOUND);
    playerRef.current = player;
    // Let the chime sound even with the phone on silent (iOS).
    setAudioModeAsync({ playsInSilentMode: true }).catch(() => {});
    return () => {
      try {
        player.release();
      } catch {
        /* already released */
      }
      playerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const onNotify = (e: MessageNotifyEvent) => {
      // Only react while the app is actually on screen; background is push's job.
      if (AppState.currentState !== 'active') return;
      const senderId = e.senderId;
      // No self-chime (server already excludes the sender; belt-and-suspenders).
      if (!senderId || senderId === myId) return;
      // No chime for a blocked user's message (isBlocked normalises the prefix).
      if (isBlocked(senderId)) return;
      // Respect per-chat «sound off» — keyed by the ChatApi channelKey (parity
      // with useChatThread.toggleMute).
      if (isSoundMuted(e.channelKey)) return;

      if (Platform.OS === 'web') {
        // Web: soft toast, scoped to community (master-circle) discussions —
        // DMs/AI chats get nothing here (unchanged from CORE-160).
        if (parseMasterProfileIdFromChannel(e.channelKey) === null) return;
        // Coalesce: if a toast is already showing, let it ride (the unread badge
        // tracks the actual count, CORE-162).
        setCommunityToast((prev) => prev ?? 'Новое сообщение в обсуждении сообщества');
        return;
      }

      // Native: chime + vibration. Во время Live-сессии — только вибрация
      // (QA FAIL 15.10.2026): по концу сигнала expo-audio гасит общую звуковую
      // сессию iOS, и вместе с ней пропадают голоса Live. Ручка выхода лежит в
      // реестре ровно пока сессия смонтирована на этом устройстве.
      if (!getActiveSessionExit()) {
        const p = playerRef.current;
        try {
          p?.seekTo(0);
          p?.play();
        } catch {
          /* ignore playback hiccups */
        }
      }
      Vibration.vibrate(40);
    };
    return chatPresenceHub.addMessageListener(onNotify);
  }, [isAuthenticated, isBlocked, myId]);

  if (Platform.OS === 'web') {
    return (
      <BrandToast
        visible={!!communityToast}
        message={communityToast || ''}
        onHide={() => setCommunityToast(null)}
        iconName="chatbubble-ellipses"
        iconColor="#E85D04"
      />
    );
  }

  return null;
}
